import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { useAdminUsers } from "@/hooks/useAdminUsers";
import { UsersFilter } from "./users/UsersFilter";
import { UsersTable } from "./users/UsersTable";
import { UserStatsCard } from "./users/UserStatsCard";
import { EditUserDialog, EditableUser } from "./users/EditUserDialog";
import { DeleteUserDialog } from "./users/DeleteUserDialog";
import { AuditLogCard } from "./users/AuditLogCard";
import { TagManagement } from "./TagManagement";
import { BulkActionsToolbar } from "./users/BulkActionsToolbar";
import { useDynamicTagsAutoSync } from "@/hooks/useDynamicTagsAutoSync";

const UsersList = () => {
  const {
    users,
    filteredUserIds,
    isLoading,
    isEmpty,
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    currentPage,
    setCurrentPage,
    totalPages,
    totalUsers,
    usersPerPage,
    sortField,
    sortDirection,
    handleSort,
    deleteUser,
    isDeleting,
    updateUser,
    isUpdating,
  } = useAdminUsers();

  // Auto-sync stale dynamic tags when this view is mounted
  useDynamicTagsAutoSync();
  const [editingUser, setEditingUser] = useState<EditableUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<EditableUser | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  const endIndex = Math.min(currentPage * usersPerPage, totalUsers);

  const handleEditSave = async (userId: string, data: Parameters<typeof updateUser>[0]["data"]) => {
    await updateUser({ userId, data });
    setEditingUser(null);
  };

  const handleDeleteConfirm = async (userId: string) => {
    await deleteUser(userId);
    setDeletingUser(null);
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const togglePageSelection = () => {
    const pageUserIds = users.map((u) => u.id);
    const allPageSelected = pageUserIds.every((id) => selectedUserIds.has(id));
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageUserIds.forEach((id) => next.delete(id));
      } else {
        pageUserIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedUserIds(new Set(filteredUserIds));
  };

  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const isPageSelected = users.length > 0 && users.every((u) => selectedUserIds.has(u.id));

   // Handler to filter by referrer name when clicking on referrer or referral count
   const handleFilterByReferrer = (referrerId: string, referrerName: string) => {
     addFilter("referredBy", referrerName);
   };
 
  return (
    <div className="space-y-4">
      <UserStatsCard />

      <EditUserDialog
        user={editingUser}
        open={!!editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
        onSave={handleEditSave}
        isSaving={isUpdating}
      />

      <DeleteUserDialog
        user={deletingUser}
        open={!!deletingUser}
        onOpenChange={(open) => !open && setDeletingUser(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
      
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-secondary" />
                Users Management
              </CardTitle>
              <CardDescription>View and manage registered users</CardDescription>
            </div>
            <UsersFilter
              filterTags={filterTags}
              addFilter={addFilter}
              removeFilter={removeFilter}
              clearAllFilters={clearAllFilters}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Bulk Actions Toolbar */}
          {selectedUserIds.size > 0 && (
            <BulkActionsToolbar
              selectedCount={selectedUserIds.size}
              totalFilteredCount={totalUsers}
              onSelectAll={selectAllFiltered}
              onClearSelection={clearSelection}
              selectedUserIds={Array.from(selectedUserIds)}
            />
          )}

          <div className="rounded-md border overflow-x-auto">
            <UsersTable
              users={users}
              isLoading={isLoading}
              isEmpty={isEmpty}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
              onEditUser={setEditingUser}
              onDeleteUser={setDeletingUser}
              selectedUserIds={selectedUserIds}
              onToggleUserSelection={toggleUserSelection}
              onTogglePageSelection={togglePageSelection}
              isPageSelected={isPageSelected}
               onFilterByReferrer={handleFilterByReferrer}
            />
          </div>

          {totalUsers > 0 && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-muted-foreground">
                {totalPages > 1 
                  ? `Showing ${(currentPage - 1) * usersPerPage + 1} to ${endIndex} of ${totalUsers} users`
                  : `Showing ${totalUsers} users of ${totalUsers}`
                }
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tag Management */}
      <TagManagement />

      {/* Activity Log */}
      <AuditLogCard />
    </div>
  );
};

export default UsersList;
